import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useStore, Panel, Badge, Meter, Empty, plural } from '../components/ui.jsx';
import { scheduleBetween, today, staffById, roomById } from '../data/store.js';
import { addDays } from '../lib/recurrence.js';

const dayLabel = (key) => new Intl.DateTimeFormat('en-US', {
  weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC',
}).format(new Date(`${key}T12:00:00Z`));

const timeLabel = (occ) => new Intl.DateTimeFormat('en-US', {
  timeZone: occ.timeZone, hour: 'numeric', minute: '2-digit',
}).format(new Date(occ.startsAt));

export default function Schedule() {
  useStore();
  const [anchor, setAnchor] = useState(today());
  const days = Array.from({ length: 7 }, (_, i) => addDays(anchor, i));
  // Cancelled dates stay visible so staff can see the gym called it off, and
  // can put it back. Silently vanishing is worse than a struck-through row.
  const occurrences = scheduleBetween(days[0], days[6], { includeCancelled: true });
  const byDay = Object.fromEntries(days.map((d) => [d, occurrences.filter((o) => o.date === d)]));

  return (
    <>
      <header className="topbar">
        <h1>Schedule</h1>
        <span className="sub">{dayLabel(days[0])} &ndash; {dayLabel(days[6])}</span>
        <div className="spacer" />
        <div className="row">
          <button onClick={() => setAnchor(addDays(anchor, -7))}>&larr; Previous</button>
          <button onClick={() => setAnchor(today())}>Today</button>
          <button onClick={() => setAnchor(addDays(anchor, 7))}>Next &rarr;</button>
        </div>
      </header>

      <div className="page">
        {days.map((day) => (
          <Panel
            key={day}
            title={dayLabel(day)}
            actions={<span className="sub">{plural(byDay[day].length, 'class')}</span>}
            bodyless
          >
            {byDay[day].length === 0 ? (
              <Empty>Nothing scheduled.</Empty>
            ) : (
              <table className="data">
                <thead>
                  <tr>
                    <th style={{ width: 90 }}>Time</th>
                    <th>Class</th>
                    <th>Coach</th>
                    <th>Room</th>
                    <th style={{ width: 170 }}>Seats</th>
                    <th className="right">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {byDay[day].map((occ) => {
                    const full = occ.booked >= occ.capacity;
                    return (
                      <tr key={occ.id} className={occ.cancelled ? 'is-cancelled' : undefined}>
                        <td className="mono num">{timeLabel(occ)}</td>
                        <td>
                          <Link to={`/schedule/${encodeURIComponent(occ.id)}`}>{occ.name}</Link>
                          {occ.substituted && <> <Badge tone="warn">sub</Badge></>}
                        </td>
                        <td>{staffById(occ.staffId)?.name ?? '—'}</td>
                        <td style={{ color: 'var(--ink-2)' }}>{roomById(occ.roomId)?.name ?? '—'}</td>
                        <td>
                          <div className="row">
                            <span className="mono num">{occ.booked}/{occ.capacity}</span>
                            <Meter value={occ.booked} max={occ.capacity} />
                          </div>
                        </td>
                        <td className="right">
                          {occ.cancelled
                            ? <Badge tone="neutral">Cancelled</Badge>
                            : full
                              ? <Badge tone="warn">{`Full${occ.waitlisted ? ` · ${occ.waitlisted} waiting` : ''}`}</Badge>
                              : <Badge tone="good">{`${occ.capacity - occ.booked} open`}</Badge>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </Panel>
        ))}
      </div>
    </>
  );
}
